import { useEffect, useRef, useState } from 'react'
import { ApiError } from '@akillidernek/api-client'
import { Button, Card, CardBody, CardHeader, Field, Input } from '@akillidernek/ui'
import { api } from '../api'
import { setAccessToken } from '../auth-store'
import { SifremiUnuttum } from './jeton-yollari'

/**
 * Giriş ekranı (YES-83) + ikinci adım (YES-115).
 *
 * Sunucu giriş ucundan ya oturum jetonu ya bir doğrulama istemi döndürüyor.
 * Ekran bunu `mfa_required` alanıyla ayırt ediyor — istemcinin "jeton var mı?"
 * diye bakması, sunucunun bir gün jetonsuz başka bir yanıt döndürmesi hâlinde
 * sessizce yanlış davranırdı.
 */
export function Giris({
  magicToken,
  onJetonTuketildi,
}: {
  /** E-postadaki giriş bağlantısından gelen jeton (YES-157). */
  magicToken?: string
  onJetonTuketildi?: () => void
} = {}) {
  const [hata, setHata] = useState<string | null>(null)
  const [sifremiUnuttum, setSifremiUnuttum] = useState(false)
  const [alanHatalari, setAlanHatalari] = useState<Record<string, string>>({})
  const [gonderiliyor, setGonderiliyor] = useState(false)
  const [baglantiGonderildi, setBaglantiGonderildi] = useState(false)
  /** Şifre doğru, ikinci adım bekleniyor. Dolu ise kod ekranı gösterilir. */
  const [istem, setIstem] = useState<string | null>(null)
  /** Magic link isteği sonuçlandı (başarılı ya da değil). */
  const [jetonBitti, setJetonBitti] = useState(false)
  // Türetilmiş durum: effect içinde senkron setState yerine. Jeton varken ve
  // yanıt gelmemişken form "doğrulanıyor" modunda.
  const jetonDogrulaniyor = Boolean(magicToken) && !jetonBitti

  // Jeton TEK kullanımlık; effect ise React StrictMode'da (geliştirmede) iki
  // kez koşar. İkinci koşu tüketilmiş jetonu görüp "geçersiz" diyordu
  // (tarayıcıda görüldü). Ref, aynı jeton için isteği bir kez atar.
  const tuketilenJeton = useRef<string | null>(null)

  // Magic link: jeton bir kez tüketilir. İkinci adım açıksa sunucu istem
  // döndürür ve aynı kod ekranı devreye girer — e-posta erişimi ikinci
  // adımın yerine geçmez.
  useEffect(() => {
    if (!magicToken || tuketilenJeton.current === magicToken) return
    tuketilenJeton.current = magicToken
    api
      .POST('/api/v1/auth/magic-link/verify', { body: { token: magicToken } })
      .then(({ data }) => {
        if (!data) return
        if ('mfa_required' in data) setIstem(data.challenge_token)
        else setAccessToken(data.token.access_token)
      })
      .catch((error: unknown) => hatayiYaz(error))
      .finally(() => {
        setJetonBitti(true)
        onJetonTuketildi?.()
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- jeton bir kez tüketilir
  }, [magicToken])

  function hatayiYaz(error: unknown) {
    if (error instanceof ApiError) {
      setHata(error.problem.detail)
      setAlanHatalari(error.fieldErrors())
    } else {
      setHata('Beklenmeyen bir hata oluştu.')
    }
  }

  async function girisYap(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    setGonderiliyor(true)
    setHata(null)
    setAlanHatalari({})
    try {
      const { data } = await api.POST('/api/v1/auth/login', {
        body: {
          email: String(form.get('email') ?? ''),
          password: String(form.get('password') ?? ''),
        },
      })
      if (!data) return
      if ('mfa_required' in data) setIstem(data.challenge_token)
      else setAccessToken(data.token.access_token)
    } catch (error) {
      hatayiYaz(error)
    } finally {
      setGonderiliyor(false)
    }
  }

  async function koduGonder(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!istem) return
    const kod = String(new FormData(event.currentTarget).get('code') ?? '')
    setGonderiliyor(true)
    setHata(null)
    try {
      const { data } = await api.POST('/api/v1/auth/login/mfa', {
        body: { challenge_token: istem, code: kod },
      })
      if (data) setAccessToken(data.token.access_token)
    } catch (error) {
      hatayiYaz(error)
    } finally {
      setGonderiliyor(false)
    }
  }

  async function baglantiIste(email: string) {
    if (!email) {
      setHata('Önce e-posta adresinizi yazın.')
      return
    }
    await api.POST('/api/v1/auth/magic-link', { body: { email } })
    // Yanıt her durumda aynı: kayıtlı olmayan e-posta belli olmasın (YES-83).
    setBaglantiGonderildi(true)
  }

  if (sifremiUnuttum) return <SifremiUnuttum onGeri={() => setSifremiUnuttum(false)} />

  if (istem) {
    return (
      /* `key` ŞART: iki ekran ağacın aynı konumunda duruyor ve alanlar
         kontrolsüz. Key olmadan React aynı <input> DOM düğümünü yeniden
         kullanıyor; kod alanı e-posta adresiyle dolu açılıyordu (tarayıcıda
         görüldü). Farklı key, React'i düğümü söküp yeniden kurmaya zorluyor. */
      <div key="mfa" className="grid min-h-dvh place-items-center p-6">
        <Card className="w-[min(26rem,100%)]">
          <CardHeader
            title="Doğrulama kodu"
            description="Kimlik doğrulayıcı uygulamanızdaki altı haneli kodu girin."
          />
          <CardBody>
            <form onSubmit={koduGonder} className="flex flex-col gap-3">
              <Field label="Kod" required>
                {({ id, invalid, required }) => (
                  <Input
                    id={id}
                    name="code"
                    autoFocus
                    // `one-time-code`: telefon, gelen SMS/uygulama kodunu
                    // klavyenin üstünde önerir.
                    autoComplete="one-time-code"
                    inputMode="text"
                    invalid={invalid}
                    required={required}
                  />
                )}
              </Field>

              <p className="text-xs text-[var(--ad-text-muted)]">
                Telefonunuza erişemiyorsanız kurtarma kodlarınızdan birini de buraya yazabilirsiniz.
              </p>

              {hata && (
                <p role="alert" className="text-xs text-[var(--ad-danger)]">
                  {hata}
                </p>
              )}

              <Button type="submit" disabled={gonderiliyor}>
                {gonderiliyor ? 'Doğrulanıyor…' : 'Doğrula'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIstem(null)
                  setHata(null)
                }}
              >
                Geri dön
              </Button>
            </form>
          </CardBody>
        </Card>
      </div>
    )
  }

  return (
    <div key="sifre" className="grid min-h-dvh place-items-center p-6">
      <Card className="w-[min(26rem,100%)]">
        <CardHeader title="Akıllı Dernek" description="Yönetim paneline giriş" />
        <CardBody>
          <form onSubmit={girisYap} className="flex flex-col gap-3">
            <Field label="E-posta" required error={alanHatalari.email}>
              {({ id, invalid, required }) => (
                <Input
                  id={id}
                  name="email"
                  type="email"
                  autoComplete="username"
                  invalid={invalid}
                  required={required}
                />
              )}
            </Field>
            <Field label="Şifre" required error={alanHatalari.password}>
              {({ id, invalid, required }) => (
                <Input
                  id={id}
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  invalid={invalid}
                  required={required}
                />
              )}
            </Field>

            {hata && (
              <p role="alert" className="text-xs text-[var(--ad-danger)]">
                {hata}
              </p>
            )}

            {jetonDogrulaniyor && (
              <p className="text-xs text-[var(--ad-text-muted)]">Giriş bağlantınız doğrulanıyor…</p>
            )}
            <Button type="submit" disabled={gonderiliyor || jetonDogrulaniyor}>
              {gonderiliyor ? 'Giriş yapılıyor…' : 'Giriş yap'}
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={(event) => {
                const form = event.currentTarget.closest('form')
                const email = new FormData(form ?? undefined).get('email')
                void baglantiIste(String(email ?? ''))
              }}
            >
              {baglantiGonderildi
                ? 'Kayıtlı hesap varsa bağlantı gönderildi'
                : 'Şifresiz giriş bağlantısı gönder'}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setSifremiUnuttum(true)}>
              Şifremi unuttum
            </Button>
          </form>
        </CardBody>
      </Card>
    </div>
  )
}
